import {useCallback, useContext} from "react";
import {useAuthHeader} from "./hooks.js";
import ConfigContext from "../context/ConfigContext";
import axios from 'axios';
import AuthContext from "../context/AuthContext";
import {ensureTrailingSlash, mergeUrl} from "./utils";
import {useSnackbar} from "notistack";

function useCallDataApi(model) {
	const {headers, formDataHeaders} = useAuthHeader();
	const {logoutUser, updateToken} = useContext(AuthContext)
	const {baseUrl} = useContext(ConfigContext);
	const {enqueueSnackbar} = useSnackbar()

	const url = mergeUrl(baseUrl, 'core',  model)

	const authenticatedAPI = axios.create({
		baseURL: url,
		headers: headers
	})

	const formDataAuthenticatedAPI = axios.create({
		baseURL: url,
		headers: formDataHeaders
	})

	const handleError = (e) => {
		if (e?.code === 'ERR_NETWORK') alert('A szolgáltatás jelenleg nem elérhető!')
		if (e.response.status === 401) {
			logoutUser()
			return
		}
		console.log(e.response)
		try {
			const messages = Object.keys(e.response?.data);
			if (messages.length < 10) enqueueSnackbar(e.response?.data?.detail, {variant: 'error'})
			else enqueueSnackbar('Váratlan hiba történt', {variant: 'error'})
		} catch (e) {
			enqueueSnackbar('Váratlan hiba történt', {variant: 'error'})
		}
	}

	const createData = useCallback(async (data) => {
			try {
				const promise = await authenticatedAPI.post("", JSON.stringify(data))
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[authenticatedAPI]
	);

	const createDataFormData = useCallback(async (data) => {
			try {
				const formData = new FormData();
				for (const key in data) {
					formData.append(key, data[key]);
				}
				const promise = await formDataAuthenticatedAPI.post("", formData)
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[formDataAuthenticatedAPI]
	);

	const postData = useCallback(async (slug, data) => {
			try {
				const promise = await authenticatedAPI.post(ensureTrailingSlash(slug), JSON.stringify(data))
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[authenticatedAPI]
	);

	const postDataFormData = useCallback(async (slug, data) => {
			try {
				const formData = new FormData();
				for (const key in data) {
					formData.append(key, data[key]);
				}
				const promise = await formDataAuthenticatedAPI.post(ensureTrailingSlash(slug), formData)
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[formDataAuthenticatedAPI]
	);

	const getData = useCallback(async (id = null) => {
			try {
				const promise = await authenticatedAPI.get(ensureTrailingSlash(id));
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[authenticatedAPI]
	);

	const getBlob = useCallback(async (id = null) => {
			try {
				const promise = await authenticatedAPI.get(ensureTrailingSlash(id), { responseType: 'blob' })
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[authenticatedAPI]
	);

	const postBlob = useCallback(async (id = null, data) => {
			try {
				const promise = await authenticatedAPI.post(ensureTrailingSlash(id), JSON.stringify(data), { responseType: 'blob' })
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[authenticatedAPI]
	);

	const updateData = useCallback(async (id, data) => {
			try {
				const promise = await authenticatedAPI.patch(ensureTrailingSlash(id), JSON.stringify(data))
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}

		},
		[authenticatedAPI]
	);

	const updateDataFormData = useCallback(async (id, data) => {
			try {
				const formData = new FormData();
				for (const key in data) {
					formData.append(key, data[key]);
				}
				const promise = await formDataAuthenticatedAPI.patch(ensureTrailingSlash(id), formData)
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}

		},
		[formDataAuthenticatedAPI]
	);

	const deleteData = useCallback(async (id) => {
			try {
				const promise = await authenticatedAPI.delete(`${id}/`)
				return promise?.data;
			} catch (error) {
				if (error.response && [401, 403].includes(error.response.status)) {
					const newToken = await updateToken();
					error.config.headers['Authorization'] = `Bearer ${newToken}`
					const newResponse = await axios(error.config)
					return newResponse?.data
				}
				handleError(error);
			}
		},
		[authenticatedAPI]
	);

	return {
		getData,
		createData,
		createDataFormData,
		updateData,
		updateDataFormData,
		deleteData,
		postData,
		postDataFormData,
		getBlob,
		postBlob
	};
}

export default useCallDataApi;